import { useState, useEffect } from 'react';
import { adminApi } from '@/lib/api';
import { FileText, Plus, Download, Mail, Search, X } from 'lucide-react';

const S = {
  font: "'Sora', system-ui, sans-serif",
  heading: "'Fraunces', Georgia, serif",
};

const gold = '#B8943A';
const goldLight = '#CCA94E';
const ink = '#2E2A26';
const sub = '#6B6560';
const cream = '#FAF8F4';
const linen = '#F2EFE8';

export default function AdminInvoicesPage() {
  const [invoices, setInvoices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'online' | 'onsite'>('all');
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [sending, setSending] = useState<string | null>(null);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (filter !== 'all') params.type = filter;
      if (search) params.search = search;
      const data = await adminApi.getInvoices(params);
      setInvoices(data);
    } catch (err) {
      console.error('Failed to fetch invoices:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchInvoices(); }, [filter]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchInvoices();
  };

  const handleDownload = (id: string, invoiceNumber: string) => {
    window.open('/api/admin/invoices/' + id + '/pdf', '_blank');
  };

  const handleEmail = async (id: string) => {
    setSending(id);
    try {
      await adminApi.emailInvoice(id);
      alert('Invoice emailed successfully!');
    } catch (err: any) {
      alert('Failed to email: ' + (err.message || 'Unknown error'));
    } finally {
      setSending(null);
    }
  };

  const formatCurrency = (amount: number, currency: string) =>
    new Intl.NumberFormat('en-IQ', { style: 'currency', currency }).format(amount);

  const formatDate = (ts: any) => {
    if (!ts) return '—';
    const d = typeof ts === 'number' ? new Date(ts * 1000) : new Date(ts);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <div style={{ fontFamily: S.font }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: S.heading, fontSize: 24, fontWeight: 700, color: ink, margin: 0 }}>Invoices</h1>
          <p style={{ color: sub, fontSize: 13, marginTop: 4 }}>{invoices.length} total invoices</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px',
            background: 'linear-gradient(135deg, ' + gold + ', ' + goldLight + ')',
            color: ink, border: 'none', borderRadius: 10, cursor: 'pointer',
            fontFamily: S.font, fontSize: 13, fontWeight: 600,
          }}
        >
          <Plus size={16} /> Create Walk-in Invoice
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        {(['all', 'online', 'onsite'] as const).map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            style={{
              padding: '6px 16px', borderRadius: 8, border: '1px solid',
              borderColor: filter === f ? gold : linen,
              background: filter === f ? gold : 'white',
              color: filter === f ? 'white' : sub,
              fontFamily: S.font, fontSize: 12, fontWeight: 600, cursor: 'pointer',
              textTransform: 'capitalize',
            }}
          >
            {f === 'all' ? 'All' : f === 'online' ? 'Online' : 'Walk-in'}
          </button>
        ))}
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
          <div style={{ position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: 9, color: sub }} />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search invoices..."
              style={{
                padding: '8px 12px 8px 32px', borderRadius: 8, border: '1px solid ' + linen,
                fontFamily: S.font, fontSize: 12, width: 220, outline: 'none',
              }}
            />
          </div>
          <button type="submit" style={{
            padding: '8px 14px', borderRadius: 8, background: ink, color: cream,
            border: 'none', fontFamily: S.font, fontSize: 12, cursor: 'pointer',
          }}>Search</button>
        </form>
      </div>

      {/* Table */}
      <div style={{ background: 'white', borderRadius: 12, border: '1px solid ' + linen, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: S.font, fontSize: 13 }}>
          <thead>
            <tr style={{ background: linen }}>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: sub, fontSize: 11 }}>INVOICE #</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: sub, fontSize: 11 }}>TYPE</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: sub, fontSize: 11 }}>CUSTOMER</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: sub, fontSize: 11 }}>COURSE</th>
              <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600, color: sub, fontSize: 11 }}>AMOUNT</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: sub, fontSize: 11 }}>DATE</th>
              <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, color: sub, fontSize: 11 }}>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ padding: 40, textAlign: 'center', color: sub }}>Loading...</td></tr>
            ) : invoices.length === 0 ? (
              <tr><td colSpan={7} style={{ padding: 40, textAlign: 'center', color: sub }}>No invoices found</td></tr>
            ) : invoices.map(inv => (
              <tr key={inv.id} style={{ borderBottom: '1px solid ' + linen }}>
                <td style={{ padding: '12px 16px', fontWeight: 600, color: ink }}>{inv.invoiceNumber}</td>
                <td style={{ padding: '12px 16px' }}>
                  <span style={{
                    padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                    background: inv.type === 'online' ? '#E8F5E9' : '#FFF3E0',
                    color: inv.type === 'online' ? '#2E7D32' : '#E65100',
                  }}>
                    {inv.type === 'online' ? 'Online' : 'Walk-in'}
                  </span>
                </td>
                <td style={{ padding: '12px 16px' }}>
                  <div style={{ fontWeight: 500, color: ink }}>{inv.customerName}</div>
                  {inv.customerEmail && <div style={{ fontSize: 11, color: sub }}>{inv.customerEmail}</div>}
                </td>
                <td style={{ padding: '12px 16px', color: sub, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inv.courseTitle}</td>
                <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600, color: ink }}>{formatCurrency(inv.subtotal, inv.currency)}</td>
                <td style={{ padding: '12px 16px', color: sub, fontSize: 12 }}>{formatDate(inv.issuedAt)}</td>
                <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                  <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                    <button onClick={() => handleDownload(inv.id, inv.invoiceNumber)} title="Download PDF"
                      style={{ padding: 6, background: 'none', border: '1px solid ' + linen, borderRadius: 6, cursor: 'pointer', color: ink }}>
                      <Download size={14} />
                    </button>
                    {inv.customerEmail && (
                      <button onClick={() => handleEmail(inv.id)} title="Email Invoice" disabled={sending === inv.id}
                        style={{ padding: 6, background: 'none', border: '1px solid ' + linen, borderRadius: 6, cursor: 'pointer', color: sending === inv.id ? sub : gold }}>
                        <Mail size={14} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create Walk-in Modal */}
      {showCreate && <CreateInvoiceModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); fetchInvoices(); }} />}
    </div>
  );
}

function CreateInvoiceModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [form, setForm] = useState({
    customerName: '', customerEmail: '', customerPhone: '',
    courseTitle: '', description: '', amount: '',
    discount: '0', tax: '0', currency: 'IQD',
    paymentMethod: 'cash', paymentStatus: 'paid', adminNotes: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await adminApi.createInvoice({
        ...form,
        amount: Number(form.amount),
        discount: Number(form.discount),
        tax: Number(form.tax),
        description: form.description || form.courseTitle,
      });
      onCreated();
    } catch (err: any) {
      alert('Failed: ' + (err.message || 'Unknown error'));
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '8px 12px', borderRadius: 8,
    border: '1px solid ' + linen, fontFamily: S.font, fontSize: 13, outline: 'none',
    boxSizing: 'border-box' as const,
  };
  const labelStyle = { display: 'block', fontSize: 11, fontWeight: 600 as const, color: sub, marginBottom: 4 };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
      <div style={{ background: 'white', borderRadius: 16, padding: 32, width: 520, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h2 style={{ fontFamily: S.heading, fontSize: 20, fontWeight: 700, color: ink, margin: 0 }}>Create Walk-in Invoice</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: sub }}><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Customer Name *</label>
              <input required value={form.customerName} onChange={e => setForm({ ...form, customerName: e.target.value })} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Email</label>
              <input type="email" value={form.customerEmail} onChange={e => setForm({ ...form, customerEmail: e.target.value })} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Phone</label>
              <input value={form.customerPhone} onChange={e => setForm({ ...form, customerPhone: e.target.value })} style={inputStyle} />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Course Title *</label>
              <input required value={form.courseTitle} onChange={e => setForm({ ...form, courseTitle: e.target.value })} style={inputStyle} />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Description</label>
              <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Defaults to course title" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Amount (IQD) *</label>
              <input required type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Discount (IQD)</label>
              <input type="number" value={form.discount} onChange={e => setForm({ ...form, discount: e.target.value })} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Payment Method</label>
              <select value={form.paymentMethod} onChange={e => setForm({ ...form, paymentMethod: e.target.value })} style={inputStyle}>
                <option value="cash">Cash</option>
                <option value="qi_card">Qi Card</option>
                <option value="bank_transfer">Bank Transfer</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Status</label>
              <select value={form.paymentStatus} onChange={e => setForm({ ...form, paymentStatus: e.target.value })} style={inputStyle}>
                <option value="paid">Paid</option>
                <option value="pending">Pending</option>
              </select>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Admin Notes</label>
              <textarea value={form.adminNotes} onChange={e => setForm({ ...form, adminNotes: e.target.value })} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={{
              padding: '10px 20px', borderRadius: 10, border: '1px solid ' + linen,
              background: 'white', color: sub, fontFamily: S.font, fontSize: 13, cursor: 'pointer',
            }}>Cancel</button>
            <button type="submit" disabled={submitting} style={{
              padding: '10px 24px', borderRadius: 10, border: 'none',
              background: 'linear-gradient(135deg, ' + gold + ', ' + goldLight + ')',
              color: ink, fontFamily: S.font, fontSize: 13, fontWeight: 600, cursor: 'pointer',
              opacity: submitting ? 0.6 : 1,
            }}>{submitting ? 'Creating...' : 'Create Invoice'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
